import "./VerificationInput.css"
import { useRef, useEffect, useMemo } from "react"


const VerificationInput = (props) => {
    const maxLength = props.maxLength || 6
    // refs for each input element
    const inputsRef = useRef([])

    // normalize inputText to an array of length maxLength
    const normalizedInput = useMemo(() => {
        const arr = Array(maxLength).fill("")
        if (Array.isArray(props.inputText) && props.inputText.length) {
            for (let i = 0; i < Math.min(props.inputText.length, maxLength); i++) {
                arr[i] = props.inputText[i] ?? ""
            }
        }
        return arr
    }, [props.inputText, maxLength])

    // helper to focus an input by index
    const focusInput = (index) => {
        const idx = Math.max(0, Math.min(maxLength - 1, index))
        const el = inputsRef.current[idx]
        if (el) el.focus()
    }

    // handle typing into a single input
    const handleChange = (e, index) => {
        const raw = e.target.value || ""
        const digit = raw.replace(/\D/g, "").slice(0, 1)
        const next = [...normalizedInput]
        next[index] = digit
        props.setInputText(next)
        // set value on element (keeps the typed character visible immediately)
        if (inputsRef.current[index]) inputsRef.current[index].value = digit
        if (digit && index < maxLength - 1) {
            focusInput(index + 1)
        }
    }

    const handleKeyDown = (e, index) => {
        const key = e.key
        const current = inputsRef.current[index]
        if (key === "Backspace") {
            e.preventDefault()
            const next = [...normalizedInput]
            if (current && current.value) {
                // if current has a value, clear it
                next[index] = ""
                props.setInputText(next)
                current.value = ""
            } else {
                // move to previous and clear
                const prevIndex = Math.max(0, index - 1)
                const prev = inputsRef.current[prevIndex]
                if (prev) {
                    prev.value = ""
                    next[prevIndex] = ""
                    props.setInputText(next)
                    focusInput(prevIndex)
                }
            }
        } else if (key === "ArrowLeft") {
            e.preventDefault()
            focusInput(index - 1)
        } else if (key === "ArrowRight") {
            e.preventDefault()
            focusInput(index + 1)
        }
    }

    // support pasting the full code
    const handlePaste = (e) => {
        e.preventDefault()
        const pasted = (e.clipboardData || window.clipboardData).getData("text") || ""
        const digits = pasted.replace(/\D/g, "").slice(0, maxLength).split("")
        if (digits.length === 0) return
        const next = Array(maxLength).fill("")
        for (let i = 0; i < digits.length; i++) next[i] = digits[i]
        props.setInputText(next)
        // update DOM values and focus next after last pasted
        for (let i = 0; i < maxLength; i++) {
            if (inputsRef.current[i]) inputsRef.current[i].value = next[i]
        }
        focusInput(Math.min(digits.length, maxLength - 1))
    }

    // when normalizedInput changes (e.g., parent resets), update DOM values
    useEffect(() => {
        for (let i = 0; i < maxLength; i++) {
            if (inputsRef.current[i]) inputsRef.current[i].value = normalizedInput[i] || ""
        }
    }, [normalizedInput, maxLength])

    // focus first input on mount
    useEffect(() => {
        setTimeout(() => focusInput(0), 0)
    }, [])

    return (
        <div id="input_container">
            {Array.from({ length: maxLength }).map((_, i) => (
                <input
                    key={i}
                    ref={(el) => (inputsRef.current[i] = el)}
                    className="form_control"
                    maxLength={1}
                    inputMode="numeric"
                    pattern="\d*"
                    aria-label={`Verification code digit ${i + 1}`}
                    autoComplete={i === 0 ? "one-time-code" : "off"}
                    onChange={(e) => handleChange(e, i)}
                    onKeyDown={(e) => handleKeyDown(e, i)}
                    onPaste={handlePaste}
                    defaultValue={normalizedInput[i] || ""}
                />
            ))}
        </div>
    )
}

export default VerificationInput